// src/controllers/useSavedCarsController.js
import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/api";
import Swal from "sweetalert2";

export default function useSavedCarsController({ setCurrentView, onAddCar } = {}) {
  const token = localStorage.getItem("token");

  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(!!token);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  // Fetch saved cars (same logic as before)
  useEffect(() => {
    if (!token) {
      setCars([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    axios
      .get(`${API_BASE_URL}/cars`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data ?? [];
        setCars(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch saved cars:", err);
        setError("ما قدرنا نجيب سياراتك المحفوظة الآن.");
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Keep exact behavior
  const goAddCar = () => {
    if (!token) return;
    if (onAddCar) onAddCar();
    else if (setCurrentView) setCurrentView("brands");
  };

  const handleDelete = async (id) => {
    if (!token) {
      await Swal.fire({
        icon: "warning",
        title: "تنبيه",
        text: "سجّل الدخول أولاً.",
        confirmButtonText: "موافق",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        customClass: {
          popup: "swal-dark-popup",
          cancelButton: "swal-cancel-btn",
        },
      });
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "هل أنت متأكد؟",
      text: "هل تريد حذف هذه السيارة من المحفوظة؟",
      showCancelButton: true,
      confirmButtonText: "نعم، احذفها",
      cancelButtonText: "إلغاء",
      confirmButtonColor: "#CAEA3E",
      iconColor: "#CAEA3E",
      reverseButtons: true,
      customClass: {
        popup: "swal-dark-popup",
        cancelButton: "swal-cancel-btn",
      },
    });

    if (!result.isConfirmed) return;

    try {
      setDeletingId(id);
      await axios.delete(`${API_BASE_URL}/cars/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCars((prev) => prev.filter((c) => c.id !== id));

      await Swal.fire({
        icon: "success",
        title: "تم الحذف",
        text: "تم حذف السيارة من المحفوظة بنجاح.",
        confirmButtonText: "موافق",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        customClass: {
          popup: "swal-dark-popup",
          cancelButton: "swal-cancel-btn",
        },
      });
    } catch (err) {
      console.error("Delete failed:", err);
      await Swal.fire({
        icon: "error",
        title: "حدث خطأ",
        text: "فشل حذف السيارة. حاول مرة ثانية.",
        confirmButtonText: "موافق",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        customClass: {
          popup: "swal-dark-popup",
          cancelButton: "swal-cancel-btn",
        },
      });
    } finally {
      setDeletingId(null);
    }
  };

  // src/components/SavedCarsModal.jsx (inside openInBrands)
  const openInBrands = (car) => {
    const payload = {
      brandId: car?.year?.model?.brand?.id ?? null,
      modelId: car?.year?.model?.id ?? null,
      yearId: car?.year?.id ?? null,
      carCategoryId: car?.pivot?.car_id ?? null,
    };

    localStorage.setItem("BRANDS_PREFILL", JSON.stringify(payload));

    // just switch views; App will force a remount
    setCurrentView && setCurrentView("brands");
  };


  const hintLogin = !token;

  return {
    token,
    cars,
    loading,
    error,
    deletingId,
    goAddCar,
    handleDelete,
    openInBrands,
    hintLogin,
  };
}
